import { FC, useState } from 'react';

import { AddDocumentIcon } from '../../../../assets/icons/index';
import { AppFormattedMessage } from '../../../../components/app-formatted-message/index';
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from '../../../../components/hover-card/index';
import { PaginationController } from '../../../../components/pagination-controller/index';
import { EventHooks, useFormat } from '../../../../hooks/index';
import { EventTypeBackend } from '../../../../types/events.types';
import { safeCapTypeTitle, Safe as SafeType } from '../../../../types/safes.types';
import { formatDisplayValue } from '../../../../utils/format-display-value.util';
import { getS3FileOriginalName } from '../../../../utils/get-s3file-original-name.util';
import { CardProps } from './transaction-card-variants.types';
import { TransactionOverviewItem } from './transaction-overview-item';
import { TransactionTitleItem } from './transaction-title-item';

export const Safe: FC<CardProps> = ({ event, companyId, openFileUploadModal }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const { format } = useFormat();
  const { items, totalPages } = EventHooks.useTransactions<SafeType>({
    eventId: event.eventId,
    eventType: EventTypeBackend.SAFE,
    companyId: companyId,
    currentPage,
  });

  if (event.type !== EventTypeBackend.SAFE) return <></>;
  return (
    <>
      {items.map(
        (
          {
            discount,
            floorValue,
            capValue,
            capType,
            issueDate,
            maturityDate,
            stakeholder,
            investment,
            filesLinks,
          },
          i,
        ) => (
          <div
            className="flex flex-col divide-y divide-gray-100 rounded-md shadow-sm"
            key={i + stakeholder.id}
          >
            <TransactionTitleItem
              createdAt={format(issueDate, 'dd/MM/yyyy')}
              name={stakeholder?.fullName}
            />
            <TransactionOverviewItem
              title={<AppFormattedMessage id="STAKEHOLDER" />}
              value={stakeholder.fullName}
            />

            <TransactionOverviewItem
              title={<AppFormattedMessage id="INVESTMENT" />}
              value={investment.toLocaleString('en-US')}
            />
            <TransactionOverviewItem
              title={<AppFormattedMessage id="CAP_VALUE" />}
              value={formatDisplayValue(capValue)}
            />
            <TransactionOverviewItem
              title={<AppFormattedMessage id="CAP_TYPE" />}
              value={
                capValue && capType ? <AppFormattedMessage id={safeCapTypeTitle[capType]} /> : '-'
              }
            />
            <TransactionOverviewItem
              title={<AppFormattedMessage id="FLOOR" />}
              value={formatDisplayValue(floorValue)}
            />
            <TransactionOverviewItem
              title={<AppFormattedMessage id="DISCOUNT" />}
              value={formatDisplayValue(discount, { multiply: 100 })}
            />

            <TransactionOverviewItem
              title={<AppFormattedMessage id="MATURITY_DATE" />}
              value={maturityDate ? format(maturityDate, 'dd/MM/yyyy') : '-'}
            />

            {filesLinks.length > 0 && (
              <TransactionOverviewItem
                title={<AppFormattedMessage id="DOCUMENTS" />}
                value={
                  <HoverCard>
                    <HoverCardTrigger onClick={() => openFileUploadModal(filesLinks)}>
                      <AddDocumentIcon className="cursor-pointer" />
                    </HoverCardTrigger>

                    <HoverCardContent
                      className="max-w-[360px] rounded-lg bg-[#101828] px-3 py-2"
                      side="top"
                    >
                      <span className="block w-full truncate p-0 text-label-md font-[550] text-white">
                        {filesLinks.length === 1
                          ? getS3FileOriginalName(filesLinks[0])
                          : `Download ${filesLinks.length} files`}
                      </span>
                    </HoverCardContent>
                  </HoverCard>
                }
              />
            )}
          </div>
        ),
      )}
      <PaginationController
        className="mt-6 shrink-0"
        currentPage={currentPage}
        onClick={setCurrentPage}
        totalPages={totalPages}
      />
    </>
  );
};
